import React, { useState, useEffect, useCallback } from 'react';
import TableBuilder, { ApiRequest, ToastBuilder, ButtonBuilder, ModalLinkBuilder } from '../../../../../../../../GlobalComponents/Builders';
import { parseJSONSafe, jsonPre } from '../../../../../../../../GlobalComponents/Helpers';

/**
 * IsolatedLogInline — Fetches and displays the correlated isolated log for a given run_id.
 * Renders inline within the activity log details modal.
 */
const IsolatedLogInline = ({ jobId, resourceId, runId }) => {
    const [logData, setLogData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetched, setFetched] = useState(false);

    const fetchLog = async () => {
        setLoading(true);
        const res = await ApiRequest('GET', '/dmt/jobs/{jobId}/isolatedLogs', {
            pathParams: { jobId },
            queryParams: { table: 'dmt_isolated_log', resource_id: resourceId, conditions: `run_id = '${runId}'`, limit: 1 },
        });
        if (res.success && res.data && res.data.length > 0) {
            let parsed = res.data[0].log_data;
            if (typeof parsed === 'string') {
                try { parsed = JSON.parse(parsed); } catch { /* keep as string */ }
            }
            setLogData(parsed);
        } else {
            setLogData(null);
        }
        setFetched(true);
        setLoading(false);
    };

    const renderLogContent = () => {
        if (!logData) return <span className="text-muted" style={{ fontSize: 12 }}>No isolated log found for this run.</span>;
        if (Array.isArray(logData)) {
            return (
                <div style={{ maxHeight: 400, overflow: 'auto', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', padding: 12, borderRadius: 6 }} className='section-group-muted'>
                    {logData.map((line, i) => {
                        if (typeof line === 'object') return <div key={i} style={{ marginBottom: 4, color: '#666' }}>{JSON.stringify(line, null, 2)}</div>;
                        const isError = typeof line === 'string' && (line.includes('ERROR') || line.includes('FAILED'));
                        return <div key={i} style={{ color: isError ? '#dc3545' : 'inherit', marginBottom: 1 }}>{String(line)}</div>;
                    })}
                </div>
            );
        }
        return jsonPre(logData);
    };

    return (
        <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between">
                <strong style={{ fontSize: 13 }}>Isolated Log:</strong>
                {!fetched && (
                    <ButtonBuilder label={loading ? 'Loading...' : 'Load Full Log'} className='btn-outline-info btn-sm' style={{ fontSize: 11 }}
                        onClick={fetchLog} useSpinner={false} disabled={loading} />
                )}
            </div>
            {fetched && <div style={{ marginTop: 6 }}>{renderLogContent()}</div>}
        </div>
    );
};

/**
 * ActivityLogViewer — Displays the dmt_sync_activity_log entries for a specific record.
 * 
 * Props:
 *  - jobId (required): The DMT job ID
 *  - resourceId (required): The resource ID
 *  - sourceRecId (required): The source record ID
 *  - limit (optional): Number of entries per page (default 20)
 *  - autoFetch (optional): Whether to fetch on mount (default true)
 */
const ActivityLogViewer = ({ jobId, resourceId, sourceRecId, limit = 20, autoFetch = true }) => {

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [totalLoaded, setTotalLoaded] = useState(0);

    const fetchLogs = useCallback(async (reset = false) => {
        if (!jobId || !resourceId || !sourceRecId) return;

        setLoading(true);
        const currentOffset = reset ? 0 : offset;

        const res = await ApiRequest('GET', '/dmt/jobs/{jobId}/records/activityLog', {
            pathParams: { jobId },
            queryParams: {
                resource_id: resourceId,
                source_rec_id: sourceRecId,
                limit: limit + 1,
                offset: currentOffset,
            },
        });

        console.log('[ActivityLogViewer] Response:', res);

        if (res.success) {
            const data = res.data || [];
            const hasMoreResults = data.length > limit;
            const trimmed = hasMoreResults ? data.slice(0, limit) : data;

            if (reset) {
                setLogs(trimmed);
                setTotalLoaded(trimmed.length);
                setOffset(limit);
            } else {
                setLogs(prev => [...prev, ...trimmed]);
                setTotalLoaded(prev => prev + trimmed.length);
                setOffset(prev => prev + limit);
            }
            setHasMore(hasMoreResults);
        } else {
            console.error('[ActivityLogViewer] Fetch failed:', res.error);
            ToastBuilder('error', res.error || 'Failed to load activity log');
        }

        setLoading(false);
    }, [jobId, resourceId, sourceRecId, limit, offset]);

    useEffect(() => {
        if (autoFetch) {
            fetchLogs(true);
        }
    }, [jobId, resourceId, sourceRecId]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'complete':
                return <span className="badge bg-success">Complete</span>;
            case 'error':
                return <span className="badge bg-danger">Error</span>;
            case 'dryRun':
                return <span className="badge bg-info text-dark">Dry Run</span>;
            default:
                return <span className="badge bg-secondary">{status || '—'}</span>;
        }
    };

    const getActionBadge = (action) => {
        switch (action) {
            case 'create':
                return <span className="badge bg-primary" style={{ opacity: 0.85 }}>Create</span>;
            case 'update':
                return <span className="badge bg-warning text-dark">Update</span>;
            case 'bypass':
                return <span className="badge bg-secondary">Bypass</span>;
            case 'error':
                return <span className="badge bg-danger">Error</span>;
            default:
                return <span className="badge bg-light text-dark">{action || '—'}</span>;
        }
    };

    const formatTimestamp = (row) => {
        if (!row.created_at) return '—';
        const d = new Date(row.created_at * 1000);
        return d.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: true, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    const getPhaseBadge = (phase) => {
        switch (phase.status) {
            case 'ok':
                return <span className="badge bg-success" style={{ fontSize: 10 }}>✓</span>;
            case 'error':
                return <span className="badge bg-danger" style={{ fontSize: 10 }}>✗</span>;
            case 'bypass':
                return <span className="badge bg-secondary" style={{ fontSize: 10 }}>—</span>;
            default:
                return <span className="badge bg-light text-dark" style={{ fontSize: 10 }}>?</span>;
        }
    };

    const renderPipeline = (pipeline) => {
        if (!pipeline || !Array.isArray(pipeline) || pipeline.length === 0) return null;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                {pipeline.map((phase, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11 }}>
                        {getPhaseBadge(phase)}
                        <span style={{ opacity: 0.8 }}>{phase.phase}</span>
                        {phase.detail && <span style={{ fontSize: 10, opacity: 0.6 }}>({phase.detail})</span>}
                        {i < pipeline.length - 1 && <span style={{ opacity: 0.3, margin: '0 2px' }}>→</span>}
                    </span>
                ))}
            </div>
        );
    };

    const renderSummary = (row) => {
        const summary = typeof row.summary === 'string' ? parseJSONSafe(row.summary) : row.summary;
        if (!summary) return <span className="text-muted">No details</span>;

        // Quick inline preview: show pipeline status
        const pipeline = summary.pipeline;
        const hasError = summary.error;
        const hasSecondary = summary.secondary_requests_added?.length > 0;
        const hasRunId = !!row.run_id;

        const previewLabel = (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                {hasError && <span style={{ color: '#d32f2f', fontSize: 11 }}>⚠ {summary.error?.phase || 'error'}</span>}
                {!hasError && pipeline && <span style={{ color: '#388e3c', fontSize: 11 }}>✓ {pipeline.length} phases</span>}
                {hasSecondary && <span style={{ fontSize: 10, opacity: 0.6 }}>+{summary.secondary_requests_added.length}</span>}
                <span style={{ color: '#1976d2', textDecoration: 'underline', fontSize: 11 }}>Details</span>
            </span>
        );

        return (
            <ModalLinkBuilder
                onSubmit={false}
                onCancelLabel="Close"
                label={previewLabel}
                title={`Activity Log — ${row.action} (${row.status})`}
                size="lg"
                body={
                    <div>
                        {/* Timestamp */}
                        <div className="mb-3" style={{ fontSize: 12, opacity: 0.7 }}>
                            {summary.timestamp || ''}
                        </div>

                        {/* Pipeline visualization */}
                        {pipeline && pipeline.length > 0 && (
                            <div className="mb-3">
                                <strong style={{ fontSize: 13 }}>Pipeline:</strong>
                                <div style={{ marginTop: 6, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-color, #e0e0e0)', background: 'var(--card-bg, transparent)' }}>
                                    {renderPipeline(pipeline)}
                                </div>
                            </div>
                        )}

                        {/* Error section */}
                        {summary.error && (
                            <div className="mb-3">
                                <strong style={{ color: '#d32f2f', fontSize: 13 }}>Error:</strong>
                                <div style={{ padding: '8px 12px', borderRadius: 4, fontSize: 12, whiteSpace: 'pre-wrap', marginTop: 4, border: '1px solid rgba(211, 47, 47, 0.3)', background: 'rgba(211, 47, 47, 0.05)' }}>
                                    <div><strong>Phase:</strong> {summary.error.phase || '—'}</div>
                                    <div style={{ marginTop: 4 }}><strong>Message:</strong> {summary.error.message || '—'}</div>
                                </div>
                            </div>
                        )}

                        {/* Correlated Isolated Log */}
                        {hasRunId && (
                            <IsolatedLogInline jobId={jobId} resourceId={resourceId} runId={row.run_id} />
                        )}

                        {/* Secondary requests */}
                        {summary.secondary_requests_added && summary.secondary_requests_added.length > 0 && (
                            <div className="mb-3">
                                <strong style={{ fontSize: 13 }}>Secondary Requests:</strong>
                                <div style={{ marginTop: 4 }}>
                                    {summary.secondary_requests_added.map((req, i) => (
                                        <span key={i} className="badge me-1 mb-1" style={{ fontSize: 11, background: 'var(--badge-bg, #e8e8e8)', color: 'var(--body-color, #333)' }}>{req}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Full JSON */}
                        <div className="mt-3 pt-3 border-top">
                            <strong style={{ fontSize: 13 }}>Full Summary:</strong>
                            {jsonPre(summary)}
                        </div>
                    </div>
                }
            />
        );
    };

    const columns = [
        {
            name: 'Timestamp',
            selector: (row) => row.created_at || 0,
            cell: (row) => <span style={{ fontSize: 12 }}>{formatTimestamp(row)}</span>,
            sortable: true,
            center: true,
            width: '25%',
            sortFunction: (a, b) => (a.created_at || 0) - (b.created_at || 0),
        },
        {
            name: 'Action',
            selector: (row) => row.action,
            cell: (row) => getActionBadge(row.action),
            sortable: true,
            center: true,
            width: '14%',
        },
        {
            name: 'Status',
            selector: (row) => row.status,
            cell: (row) => getStatusBadge(row.status),
            sortable: true,
            center: true,
            width: '14%',
        },
        {
            name: 'Details',
            cell: (row) => renderSummary(row),
            center: true,
            width: '47%',
        },
    ];

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">
                    {totalLoaded > 0 ? `Showing ${totalLoaded} entr${totalLoaded === 1 ? 'y' : 'ies'}` : ''}
                </small>
                <ButtonBuilder
                    label="Refresh"
                    className="btn-outline-secondary"
                    size="sm"
                    onClick={() => fetchLogs(true)}
                    useSpinner={false}
                    style={{ fontSize: 11 }}
                />
            </div>
            <TableBuilder
                data={logs}
                columns={columns}
                processing={loading}
                pagination={false}
                sortField={1}
                sortOrderAsc={false}
            />
            {hasMore && (
                <div className="text-center mt-2">
                    <ButtonBuilder
                        label={loading ? 'Loading...' : 'Load More'}
                        className="btn-outline-primary"
                        size="sm"
                        onClick={() => fetchLogs(false)}
                        useSpinner={false}
                        disabled={loading}
                    />
                </div>
            )}
            {!loading && logs.length === 0 && (
                <div className="text-center py-3">
                    <small className="text-muted">No activity log entries found for this record.</small>
                </div>
            )}
        </div>
    );
};

export default ActivityLogViewer;
